import React, { useEffect, useState } from "react";
import { Menu, X, Volume2, VolumeX } from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";
import { Logo } from "./Logo";
import { LanguageSelector } from "./LanguageSelector";
import { useLanguage } from "../contexts/LanguageContext";
import { TELEGRAM_BASE, generateTelegramLink } from "../data/telegramLinks";

export const Header = ({ soundOn, setSoundOn }) => {
  const { t } = useLanguage();
  const [scrolled, setScrolled] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    window.addEventListener("scroll", onScroll, { passive: true });
    onScroll();
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const navLinks = [
    { href: "#accesses", label: t("navAccesses"), id: "nav-accesses" },
    { href: "#help", label: t("navHelp"), id: "nav-help" },
    { href: "#codes", label: t("navCodes"), id: "nav-codes" },
    { href: "#reviews", label: t("navReviews"), id: "nav-reviews" },
    { href: "#how", label: t("navHow"), id: "nav-how" },
    { href: "#faq", label: t("navFAQ"), id: "nav-faq" },
  ];

  const onboardLink = generateTelegramLink("private-support");

  return (
    <header
      className={`fixed top-0 inset-x-0 z-50 transition-all duration-500 ${
        scrolled ? "py-3 glass-strong border-b border-[#D7B56D]/10" : "py-5 bg-transparent"
      }`}
      data-testid="site-header"
    >
      <div className="max-w-[1440px] mx-auto px-5 md:px-10 lg:px-16 flex items-center justify-between gap-6">
        <a href="#top" className="shrink-0" data-testid="header-logo-link">
          <Logo />
        </a>

        <nav className="hidden lg:flex items-center gap-7">
          {navLinks.map((link) => (
            <a
              key={link.id}
              href={link.href}
              className="text-[13px] font-medium text-[#D8DCE2] hover:text-[#D7B56D] transition-colors tracking-wide"
              data-testid={link.id}
            >
              {link.label}
            </a>
          ))}
        </nav>

        <div className="flex items-center gap-2 md:gap-3 shrink-0">
          <button
            onClick={() => setSoundOn(!soundOn)}
            className="hidden md:flex items-center justify-center w-9 h-9 rounded-full glass border border-[#D8DCE2]/10 hover:border-[#D7B56D]/40 transition-colors"
            aria-label={soundOn ? t("soundOn") : t("soundOff")}
            data-testid="sound-toggle-btn"
          >
            {soundOn ? <Volume2 size={14} className="text-[#76D9E8]" /> : <VolumeX size={14} className="text-[#D8DCE2]" />}
          </button>

          <LanguageSelector />

          <a
            href={onboardLink}
            target="_blank"
            rel="noopener noreferrer"
            className="hidden md:inline-flex btn-gold !py-2.5 !px-5 text-[12px]"
            data-testid="header-cta-btn"
          >
            {t("ctaRequestOnboarding")}
          </a>

          <button
            onClick={() => setMenuOpen(true)}
            className="lg:hidden flex items-center justify-center w-10 h-10 rounded-full glass border border-[#D8DCE2]/10 relative z-[55]"
            aria-label="Open menu"
            data-testid="mobile-menu-btn"
          >
            <Menu size={16} className="text-[#F5F1EA]" />
          </button>
        </div>
      </div>

      <AnimatePresence>
        {menuOpen && (
          <motion.div
            className="fixed inset-0 z-[60] lg:hidden"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            data-testid="mobile-menu-drawer"
          >
            <div
              className="absolute inset-0 bg-[#07090D]/90 backdrop-blur-lg"
              onClick={() => setMenuOpen(false)}
            />
            <motion.div
              initial={{ x: "100%" }}
              animate={{ x: 0 }}
              exit={{ x: "100%" }}
              transition={{ type: "spring", damping: 30 }}
              className="absolute right-0 top-0 bottom-0 w-[88%] max-w-[400px] glass-strong p-7 flex flex-col"
            >
              <div className="flex items-center justify-between mb-10">
                <Logo />
                <button
                  onClick={() => setMenuOpen(false)}
                  className="w-10 h-10 rounded-full bg-[#1A1C22] flex items-center justify-center"
                  data-testid="mobile-menu-close-btn"
                  aria-label="Close menu"
                >
                  <X size={18} className="text-[#F5F1EA]" />
                </button>
              </div>
              <nav className="flex flex-col gap-1">
                {navLinks.map((link) => (
                  <a
                    key={link.id}
                    href={link.href}
                    onClick={() => setMenuOpen(false)}
                    className="py-4 px-2 text-lg font-display font-semibold text-[#F5F1EA] border-b border-[#D8DCE2]/8 hover:text-[#D7B56D] transition-colors"
                    data-testid={`m-${link.id}`}
                  >
                    {link.label}
                  </a>
                ))}
              </nav>
              <div className="mt-auto pt-8">
                <a
                  href={onboardLink}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="btn-gold w-full"
                  data-testid="mobile-menu-cta"
                >
                  {t("ctaRequestOnboarding")}
                </a>
                <p className="text-[11px] text-[#D8DCE2]/55 mt-4 leading-relaxed">{t("disclaimer")}</p>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </header>
  );
};
